import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { teacherPaymentService } from '../../services/teacherPaymentService';
import { teacherService } from '../../services/api';
import { schoolYearService } from '../../services/schoolYearService';
import { exportToExcel } from '../../utils/excelExport';
import dayjs from 'dayjs';
import { formatCurrency } from '../../utils/formatNumber';

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const TeacherPaymentList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({
    total_paid: 0,
    total_hours: 0,
    total_payments: 0
  });
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    month: '',
    year: dayjs().year(),
    status: '',
    teacher_id: '',
    school_year_id: ''
  });
  
  const [teachers, setTeachers] = useState<any[]>([]);
  const [schoolYears, setSchoolYears] = useState<any[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    loadTeachers();
    loadSchoolYears();
  }, []);

  useEffect(() => {
    loadPayments();
  }, [paginationModel, filters]);

  const loadTeachers = async () => {
    try {
      const response = await teacherService.getAll({ per_page: 100 });
      setTeachers(response.data.data || []);
    } catch (err) {
      console.error('Error loading teachers:', err);
    }
  };

  const loadSchoolYears = async () => {
    try {
      const response = await schoolYearService.getAll({ per_page: 100 });
      setSchoolYears(response.data.data || []);
      
      // Seleccionar curso activo por defecto
      const active = response.data.data.find((y: any) => y.is_active);
      if (active && !filters.school_year_id) {
        setFilters(prev => ({ ...prev, school_year_id: active.id }));
      }
    } catch (err) {
      console.error('Error loading school years:', err);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      // Limpiar filtros vacíos
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await teacherPaymentService.getAll(params);
      
      setPayments(response.data.payments?.data || []);
      setTotal(response.data.payments?.total || 0);
      setTotals(response.data.totals || { total_paid: 0, total_hours: 0, total_payments: 0 });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPayment) return;
    
    try {
      await teacherPaymentService.delete(selectedPayment.id);
      loadPayments();
      setDeleteDialogOpen(false);
      setSelectedPayment(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar pago');
    }
  };

  const handleExport = () => {
    const data = payments.map(p => ({
      'ID': p.id,
      'Profesor': p.teacher ? `${p.teacher.first_name} ${p.teacher.last_name}` : 'N/A',
      'Período': `${p.month} ${p.year}`,
      'Horas': p.hours_worked,
      'Tarifa/Hora': p.hourly_rate,
      'Total': p.total_amount,
      'Fecha Pago': p.payment_date ? dayjs(p.payment_date).format('DD/MM/YYYY') : '',
      'Estado': p.status === 'paid' ? 'Pagado' : p.status === 'pending' ? 'Pendiente' : 'Cancelado',
      'Descripción': p.description || ''
    }));
    
    exportToExcel(data, 'pagos_profesores');
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'paid':
        return <Chip icon={<CheckCircleIcon />} label="Pagado" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<ErrorIcon />} label="Pendiente" color="warning" size="small" />;
      case 'cancelled':
        return <Chip icon={<WarningIcon />} label="Cancelado" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'teacher',
      headerName: 'Profesor',
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.teacher?.first_name} {params.row.teacher?.last_name}
          </Typography>
          {params.row.teacher?.specialization && (
            <Typography variant="caption" color="text.secondary">
              {params.row.teacher.specialization}
            </Typography>
          )}
        </Box>
      )
    },
    {
        field: 'period',
        headerName: 'Período',
        flex: .4,
        valueGetter: (value, row) => {
            // En versiones recientes de Data Grid, valueGetter recibe (value, row)
            return `${row.month} ${row.year}`;
        }
    },
    {
      field: 'hours_worked',
      headerName: 'Horas habituales',
      flex: .5,
      align: 'center',
      renderCell: (params) => (
        <Chip
          icon={<AccessTimeIcon />}
          label={params.value}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'hourly_rate',
      headerName: 'Tarifa',
      flex: .4,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2">
          ₽{params.value}
        </Typography>
      )
    },
    {
      field: 'hours_extra',
      headerName: 'Horas extra',
      flex: .4,
      align: 'center',
      renderCell: (params) => (
        <Chip
          icon={<AccessTimeIcon />}
          label={params.value}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'hourly_rate_extra',
      headerName: 'Tarifa horas extra',
      flex: .4,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2">
          ₽{params.value}
        </Typography>
      )
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      flex: .6,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          ₽{params.value.toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'payment_date',
      headerName: 'Fecha Pago',
      flex: .6,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.payment_date 
            ? dayjs(params.row.payment_date).format('DD/MM/YYYY')
            : '-'
          }
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: .4,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: .6,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teacher-payments/${params.row.id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/teacher-payments/${params.row.id}/edit`);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === 'pending' && (
            <Tooltip title="Marcar como pagado">
              <IconButton
                size="small"
                color="success"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await teacherPaymentService.markAsPaid(params.row.id);
                    loadPayments();
                  } catch (err) {
                    console.error('Error marking as paid:', err);
                  }
                }}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPayment(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Pagos a Profesores
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/teacher-payments/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nuevo Pago
          </Button>
        </Box>
      </Box>

      {/* Totales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Pagado
              </Typography>
              <Typography variant="h4" color="primary">
                ₽{formatCurrency(totals.total_paid)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Horas
              </Typography>
              <Typography variant="h4">
                {totals.total_hours}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Pagos
              </Typography>
              <Typography variant="h4">
                {totals.total_payments}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Profesor</InputLabel>
              <Select
                value={filters.teacher_id}
                label="Profesor"
                onChange={(e) => setFilters({...filters, teacher_id: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                {teachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.first_name} {teacher.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Mes</InputLabel>
              <Select
                value={filters.month}
                label="Mes"
                onChange={(e) => setFilters({...filters, month: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                {months.map(month => (
                  <MenuItem key={month} value={month}>{month}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Año"
              value={filters.year}
              onChange={(e) => setFilters({...filters, year: parseInt(e.target.value)})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="paid">Pagado</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Curso</InputLabel>
              <Select
                value={filters.school_year_id}
                label="Curso"
                onChange={(e) => setFilters({...filters, school_year_id: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                {schoolYears.map((year) => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => loadPayments()}
                fullWidth
                size="small"
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({
                    month: '',
                    year: dayjs().year(),
                    status: '',
                    teacher_id: '',
                    school_year_id: schoolYears.find(y => y.is_active)?.id || ''
                  });
                }}
                size="small"
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={payments}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/teacher-payments/${params.id}`)}
          disableRowSelectionOnClick
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro de eliminar este pago?
          </Typography>
          {selectedPayment && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Profesor:</strong> {selectedPayment.teacher?.first_name} {selectedPayment.teacher?.last_name}
              </Typography>
              <Typography variant="body2">
                <strong>Período:</strong> {selectedPayment.month} {selectedPayment.year}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> ₽{selectedPayment.total_amount}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherPaymentList;